import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import { collection, addDoc, doc, getDocs, updateDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import Modal from 'react-modal';
import Logo from '../images/school_logo.png';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';

Modal.setAppElement('#root');

const Sections = () => {
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [update, setUpdate] = useState(false);
  const [sections, setSections] = useState([]);
  const [filteredSections, setFilteredSections] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [newSession, setNewSession] = useState({ name: '' });
  const [editingSession, setEditingSession] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [currentSection, setCurrentSection] = useState(null);
  const [isFetchingSections, setIsFetchingSections] = useState(true);
  const [openDays, setOpenDays] = useState({
    sunday: false,
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false
  });

  const sectionsCollectionRef = collection(db, "sections");

  useEffect(() => {
    fetchSections();
  }, []);

  useEffect(() => {
    const filtered = sections.filter((section) =>
      section.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredSections(filtered);
  }, [searchQuery, sections]);

  const fetchSections = async () => {
    setIsFetchingSections(true);
    try {
      const data = await getDocs(sectionsCollectionRef);
      const fetchedSections = data.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      setSections(fetchedSections);
    } catch (error) {
      setError("Failed to fetch sections");
      setShowModal(true);
    } finally {
      setIsFetchingSections(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewSession({ ...newSession, [name]: value });
  };

  const handleDayChange = (day) => {
    setOpenDays({ ...openDays, [day]: !openDays[day] });
  };

  const handleAddSession = async () => {
    if (!newSession.name.trim()) {
      setError("Section name cannot be empty");
      setShowModal(true);
      return;
    }
    setUpdate(true);
    try {
      await addDoc(sectionsCollectionRef, {
        ...newSession,
        openDays: openDays,
        createdAt: Timestamp.now()
      });
      setSuccessMessage("Section added successfully");
      fetchSections();
      setNewSession({ name: '' });
      setOpenDays({ // Reset days selection
        sunday: false,
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false
      });
      setIsAddModalOpen(false);
    } catch (error) {
      setError("Failed to add section");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleUpdateSession = async () => {
    setUpdate(true);
    try {
      const sessionDoc = doc(db, "sections", editingSession.id);
      // await updateDoc(sessionDoc, newSession);
      await updateDoc(sessionDoc, {
        name: newSession.name,
        openDays: openDays // Update the days
      });
      setSuccessMessage("Section updated successfully");
      fetchSections();
      setEditingSession(null);
      setNewSession({ name: '' });
      setIsEditModalOpen(false);
    } catch (error) {
      setError("Failed to update section");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleEditSession = (sessionToEdit) => {
    setEditingSession(sessionToEdit);
    setNewSession({ name: sessionToEdit.name });
    setOpenDays(sessionToEdit.openDays || { // Load existing days or default to all false
      sunday: false,
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false
    });
    setIsEditModalOpen(true);
  };

  const handleDeleteSession = async (id) => {
    setUpdate(true);
    try {
      const sessionDoc = doc(db, "sections", id);
      await deleteDoc(sessionDoc);
      setIsDeleteModalOpen(false);
      setSuccessMessage("Section deleted successfully");
      fetchSections();
    } catch (error) {
      setError("Failed to delete section");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const openDeleteModal = (section) => {
    setCurrentSection(section);
    setIsDeleteModalOpen(true);
  };

  if (loading) {
    return <Loader text={"LOADING"} />;
  }

  if (!userData) {
    return <Loader text={"READING"} />;
  }

  const allowedRoles = ['Admin', 'Cashier'];

  return (
    <>
      {update && <Loader text={"PROCESSING"} />}
      {allowedRoles.includes(userData.status) ? (
        <>
          <div className="top-elements row align-center space-between">
            <Link to="/home" className="back-link">
              <i className="fa-solid fa-arrow-left size3"></i>
            </Link>
            <div className="column align-center justify-center">
              <img src={Logo} className="middle-logo" alt="logo" />
            </div>
            <Link to="#" className="home-link" onClick={() => fetchSections()}>
              <i className="fa-solid fa-rotate-right size4"></i>
            </Link>
          </div>

          <div className="login-page-items column align-center scroll-fixed">
            <h2 className="manage-page-heading caps size4">Manage Sections</h2>
              <button className="login-button login-button-d caps" onClick={() => setIsAddModalOpen(true)}>Add Section</button>

            <input
              type="text"
              className="input-field1"
              placeholder="Search by name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            <div className="scroll-fixed scroll-fixed1">
              <div className="class-list class-list-d scrollable">
                {isFetchingSections ? (
                  <p className="loading-text">Loading sections ...</p>
                ) : filteredSections.length === 0 ? (
                  <p className="loading-text">No sections found</p>
                ) : (
                  filteredSections.map((sessionItem) => (
                    <div key={sessionItem.id} className="class-item">
                      <p>{sessionItem.name}</p>
                      <div className="row align-center justify-center gap-15">
                        <button className="list-btn" onClick={() => handleEditSession(sessionItem)}><i className="fas fa-edit"></i></button>
                        {/* <button className="list-btn" onClick={() => openDeleteModal(sessionItem)}><i className="fas fa-trash-alt"></i></button> */}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
       </>
      ) : (
        <p className="male-female-stat align-center justify-center">You don't have permission to view this page.</p>
      )}

      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => {
            setError("");
            setSuccessMessage("");
            setShowModal(false);
          }}
        />
      )}

      {/* Add Section Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onRequestClose={() => setIsAddModalOpen(false)}
        contentLabel="Add Section"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Add Section</h2>
        <form className="session-form">
          <label className='size5 bold'>Name</label>
          <input
            type="text"
            name="name"
            className="input-field"
            placeholder="Section Name"
            value={newSession.name}
            onChange={handleInputChange}
          />

          {/* Add this inside the Add Modal form, after the name input */}
          <div className="days-selection">
            <label className='size5 bold'>School Open Days</label>
            <div className="column">
              {['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].map((day) => (
                <label key={day} className="day-checkbox">
                  <input
                    type="checkbox"
                    checked={openDays[day]}
                    onChange={() => handleDayChange(day)}
                  />
                  <span className="day-name">{day.charAt(0).toUpperCase() + day.slice(1)}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="row align-center gap-1">
            <button className="login-button" type="button" onClick={() => setIsAddModalOpen(false)}>Cancel</button>
            <button className="login-button" type="button" onClick={handleAddSession}>Add Section</button>
          </div>
        </form>
      </Modal>

      {/* Edit Section Modal */}
      <Modal
        isOpen={isEditModalOpen}
        // onRequestClose={() => setIsEditModalOpen(false)}
        onRequestClose={() => {
          setIsEditModalOpen(false);
          setNewSession({ name: '' }); // Reset newSession
          setEditingSession(null); // Reset editingSession
        }}
        contentLabel="Edit Section"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Edit Section</h2>
        <form className="session-form">
          <label className='size5 bold'>Name</label>
          <input
            type="text"
            name="name"
            className="input-field"
            placeholder="Section Name"
            value={newSession.name}
            onChange={handleInputChange}
          />
          {/* Add this inside the Edit Modal form, after the name input */}
          <div className="days-selection">
            <label className='size5 bold'>School Open Days</label>
            <div className="column">
              {['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].map((day) => (
                <label key={day} className="day-checkbox">
                  <input
                    type="checkbox"
                    checked={openDays[day]}
                    onChange={() => handleDayChange(day)}
                  />
                  <span className="day-name">{day.charAt(0).toUpperCase() + day.slice(1)}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="row align-center justify-center gap-1">
            <button className="login-button" type="button" onClick={() => {
              setIsEditModalOpen(false);
              setNewSession({ name: '' }); // Reset newSession
              setEditingSession(null); // Reset editingSession
            }}>Cancel</button>
            <button className="login-button" type="button" onClick={handleUpdateSession}>Update Section</button>
          </div>
        </form>
      </Modal>

      {/* Delete Section Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onRequestClose={() => setIsDeleteModalOpen(false)}
        contentLabel="Delete Section"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Delete Section</h2>
        {currentSection && (
          <div>
            <p>Are you sure you want to delete <strong>{currentSection.name}</strong>?</p>
            <div className="row align-center gap-1">
              <button className="login-button" type="button" onClick={() => setIsDeleteModalOpen(false)}>Cancel</button>
              <button className="login-button" type="button" onClick={() => handleDeleteSession(currentSection.id)}>Delete</button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default Sections;
